/**
 * Canvas-generated textures for the live viewer (port of design/shared/viewer.js):
 * ply edge section, procedural face fallback, radial glow and contact-shadow alpha maps.
 */

export const clamp01 = (t: number): number => Math.max(0, Math.min(1, t));

/** FNV-1a 32-bit string hash. */
export function hashStr(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** Seeded PRNG (mulberry32) returning [0, 1). */
export function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function canvas2d(w: number, h: number): { c: HTMLCanvasElement; g: CanvasRenderingContext2D } {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const g = c.getContext('2d');
  if (!g) throw new Error('2D canvas is not available');
  return { c, g };
}

// ---------------------------------------------------------------------------
// Ply edge texture: a layered section drawn on a canvas (13 plies for 18 mm).
// u runs along the edge, v across the thickness (0..1 = face to face).
// ---------------------------------------------------------------------------
export function makePlyEdgeCanvas(plies: number, seed: number, w = 1024, h = 512): HTMLCanvasElement {
  const { c, g } = canvas2d(w, h);
  const r = rng(seed);
  // ply thicknesses with a little variation; outer veneers slightly thinner
  const weights = Array.from({ length: plies }, (_, i) => (i === 0 || i === plies - 1 ? 0.82 : 1) * (0.92 + r() * 0.16));
  const total = weights.reduce((a, b) => a + b, 0);
  let y = 0;
  const bands = weights.map((wt, i) => {
    const b = { y0: y, y1: y + (wt / total) * h, i };
    y = b.y1;
    return b;
  });
  for (const b of bands) {
    const along = b.i % 2 === 0; // face-direction plies alternate with cross plies
    const base = along ? [242, 224, 190] : [206, 168, 118];
    const jitter = (r() - 0.5) * 14;
    g.fillStyle = `rgb(${base[0] + jitter},${base[1] + jitter * 0.9},${base[2] + jitter * 0.7})`;
    g.fillRect(0, b.y0, w, b.y1 - b.y0);
    const bh = b.y1 - b.y0;
    if (along) {
      // long-grain ply: faint streaks along u
      for (let k = 0; k < 70; k++) {
        const yy = b.y0 + r() * bh;
        g.strokeStyle = `rgba(150,110,62,${0.05 + r() * 0.08})`;
        g.lineWidth = 0.6 + r() * 1.2;
        const x0 = r() * w,
          len = 60 + r() * 380;
        g.beginPath();
        g.moveTo(x0, yy);
        g.lineTo(x0 + len, yy + (r() - 0.5) * 2);
        g.stroke();
      }
    } else {
      // cross-grain ply: end grain, short pores across the band
      for (let k = 0; k < 900; k++) {
        const xx = r() * w,
          yy = b.y0 + r() * bh;
        g.fillStyle = `rgba(128,88,48,${0.06 + r() * 0.12})`;
        g.fillRect(xx, yy, 0.8 + r() * 1.2, 1 + r() * Math.min(5, bh * 0.25));
      }
      const grd = g.createLinearGradient(0, b.y0, 0, b.y1);
      grd.addColorStop(0, 'rgba(120,80,40,.10)');
      grd.addColorStop(0.5, 'rgba(120,80,40,0)');
      grd.addColorStop(1, 'rgba(120,80,40,.10)');
      g.fillStyle = grd;
      g.fillRect(0, b.y0, w, bh);
    }
  }
  // glue lines between plies
  for (let i = 1; i < bands.length; i++) {
    const yy = bands[i].y0;
    g.fillStyle = 'rgba(84,54,26,.7)';
    g.fillRect(0, yy - 1.5, w, 3);
    g.fillStyle = 'rgba(84,54,26,.2)';
    g.fillRect(0, yy - 3, w, 6);
  }
  // occasional small voids/patches typical of B/BB edges, very subtle
  for (let k = 0; k < 6; k++) {
    const b = bands[1 + Math.floor(r() * (bands.length - 2))];
    g.fillStyle = 'rgba(90,60,30,.25)';
    g.fillRect(r() * w, b.y0 + (b.y1 - b.y0) * 0.35, 6 + r() * 18, (b.y1 - b.y0) * 0.3);
  }
  return c;
}

/** Fallback face texture if the CC0 scan cannot be loaded. */
export function makeProceduralFaceCanvas(seed = 7, s = 1024): HTMLCanvasElement {
  const { c, g } = canvas2d(s, s);
  const r = rng(seed);
  g.fillStyle = 'rgb(234,216,184)';
  g.fillRect(0, 0, s, s);
  for (let k = 0; k < 520; k++) {
    const y = r() * s,
      amp = 4 + r() * 22,
      ph = r() * 6.28,
      fq = 0.004 + r() * 0.01;
    g.strokeStyle = `rgba(${170 + r() * 30},${130 + r() * 20},${80 + r() * 20},${0.04 + r() * 0.07})`;
    g.lineWidth = 0.5 + r() * 2.5;
    g.beginPath();
    for (let x = -8; x <= s + 8; x += 8) {
      const yy = y + Math.sin(x * fq + ph) * amp;
      if (x < 0) g.moveTo(x, yy);
      else g.lineTo(x, yy);
    }
    g.stroke();
  }
  return c;
}

export function makeRadialCanvas(size = 256, inner = 0.0, stops: Array<[number, number]> = [[0, 1], [1, 0]]): HTMLCanvasElement {
  const { c, g } = canvas2d(size, size);
  const grd = g.createRadialGradient(size / 2, size / 2, size * inner, size / 2, size / 2, size / 2);
  // alpha maps read the green channel: draw grey levels on black
  for (const [o, a] of stops) {
    const v = Math.round(a * 255);
    grd.addColorStop(o, `rgb(${v},${v},${v})`);
  }
  g.fillStyle = '#000';
  g.fillRect(0, 0, size, size);
  g.fillStyle = grd;
  g.fillRect(0, 0, size, size);
  return c;
}

/** Soft rectangular contact shadow (alpha map, white = dark). */
export function makeContactCanvas(size = 256): HTMLCanvasElement {
  const { c, g } = canvas2d(size, size);
  const img = g.createImageData(size, size);
  for (let y = 0; y < size; y++)
    for (let x = 0; x < size; x++) {
      const u = Math.abs(x / (size - 1) - 0.5) * 2,
        v = Math.abs(y / (size - 1) - 0.5) * 2;
      const d = Math.max(0, Math.max(u, v) - 0.62) / 0.38; // 0 inside footprint, 1 at the border
      const a = Math.pow(1 - clamp01(d), 2.2);
      const i = (y * size + x) * 4;
      img.data[i] = img.data[i + 1] = img.data[i + 2] = Math.round(a * 255);
      img.data[i + 3] = 255;
    }
  g.putImageData(img, 0, 0);
  return c;
}

/** Vertical gradient for the wall/floor corner shading (white at the top, black at the bottom). */
export function makeCornerCanvas(): HTMLCanvasElement {
  const { c, g } = canvas2d(4, 256);
  const grd = g.createLinearGradient(0, 0, 0, 256);
  grd.addColorStop(0, '#fff');
  grd.addColorStop(0.3, '#5a5a5a');
  grd.addColorStop(1, '#000');
  g.fillStyle = grd;
  g.fillRect(0, 0, 4, 256);
  return c;
}
